import { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import { Logout, ArrowBack, HelpOutline, Lock, Person, AdminPanelSettings } from '@mui/icons-material';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { authService } from '@/services/firebase/auth';
import { ChangePasswordDialog } from '@/components/dialogs/ChangePasswordDialog';
import { ProfileDialog } from '@/components/dialogs/ProfileDialog';
import { isAdminDomain } from '@/utils/adminAccess';
import type { ReactNode } from 'react';

type AppLayoutProps = {
  children: ReactNode;
  title?: string;
  showBackToDashboard?: boolean;
  backToSection?: 'thinkspaces' | 'todolists' | 'notebooks';
  showLogo?: boolean;
};

export const AppLayout = ({ children, title, showBackToDashboard, backToSection, showLogo }: AppLayoutProps) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const isEmailUser = authService.isEmailUser();
  const isAdmin = isAdminDomain(user?.email);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleMenuClose();
    setProfileOpen(true);
  };

  const handleChangePassword = () => {
    handleMenuClose();
    setChangePasswordOpen(true);
  };

  const handleSignOut = async () => {
    handleMenuClose();
    await signOut();
    navigate('/');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="fixed" color="default" elevation={1}>
        <Toolbar sx={{ gap: 2 }}>
          {showBackToDashboard && (
            <IconButton
              onClick={() => navigate('/dashboard', { state: { section: backToSection } })}
              sx={{ mr: 1 }}
              aria-label="Back to dashboard"
            >
              <ArrowBack />
            </IconButton>
          )}

          {showLogo ? (
            <Box
              component="img"
              src="/images/logo.png"
              alt="Thinkspace"
              sx={{
                height: 40,
                flexGrow: 1,
                objectFit: 'contain',
                objectPosition: 'left',
              }}
            />
          ) : (
            <Typography
              variant="h6"
              component="h1"
              sx={{
                flexGrow: 1,
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {title || 'Thinkspace'}
            </Typography>
          )}

          {user && (
            <>
              <IconButton
                onClick={() => navigate('/help')}
                size="small"
                aria-label="Help"
                sx={{ mr: 1 }}
              >
                <HelpOutline />
              </IconButton>
              <IconButton onClick={handleMenuOpen} size="small">
                <Avatar
                  src={user.photoURL || undefined}
                  alt={user.displayName || 'User'}
                  sx={{ width: 36, height: 36 }}
                >
                  {user.displayName?.[0] || user.email?.[0] || 'U'}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem disabled>
                  <Typography variant="body2" color="text.secondary">
                    {user.email}
                  </Typography>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleProfile}>
                  <Person fontSize="small" sx={{ mr: 1 }} />
                  Profile
                </MenuItem>
                {isEmailUser && (
                  <MenuItem onClick={handleChangePassword}>
                    <Lock fontSize="small" sx={{ mr: 1 }} />
                    Change Password
                  </MenuItem>
                )}
                {isAdmin && (
                  <MenuItem onClick={() => { handleMenuClose(); navigate('/admin/users'); }}>
                    <AdminPanelSettings fontSize="small" sx={{ mr: 1 }} />
                    Admin Users
                  </MenuItem>
                )}
                <Divider />
                <MenuItem onClick={handleSignOut}>
                  <Logout fontSize="small" sx={{ mr: 1 }} />
                  Sign Out
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>

      <Box component="main" sx={{ flexGrow: 1, pt: '64px' }}>
        {children}
      </Box>

      {/* Dialogs */}
      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
      <ProfileDialog
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
      />
    </Box>
  );
};
